import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Calendar, Info, Loader2 } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Checkbox } from '../../../components/ui/checkbox';
import { MultiSelect } from '../../../components/ui/multi-select';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import { Tooltip } from '../../../components/ui/tooltip';
import { cn } from '../../../lib/utils';
import type { DashboardProject } from '../../project/types/project';
import {
  fetchPipeStatusOverview,
  fetchPipeUploadActivity,
} from '../api/pipeStatusOverview';
import {
  encodeOverviewGroupIdForQuery,
  formatOverviewGroupLabel,
  pipeEndPresenceFlagSuffixText,
  PIPE_STATUS_UNGROUPED_TOKEN,
  PIPE_UPLOAD_ACTIVITY_TIME_RANGE_LABELS,
  type OverviewEndPresenceFilter,
  type OverviewFlagFilter,
  type PipeStatusOverviewBucket,
  type PipeStatusOverviewResponse,
  type PipeUploadActivityResponse,
  type PipeUploadActivityTimeRange,
} from '../api/pipeStatusOverviewTypes';
import { PipeUploadActivityInsights } from './PipeUploadActivityInsights';

export interface OverviewPipeSizeOption {
  pipeId: string | number;
  label: string;
}

interface PipeStatusOverviewPanelProps {
  project: DashboardProject;
  pipeSizeOptions: OverviewPipeSizeOption[];
  onOpenGroups: (args: { groupIds: string[]; pipeIds: Array<string | number> }) => void;
  refreshToken?: number;
}

const TIME_RANGE_OPTIONS: PipeUploadActivityTimeRange[] = ['all', '30', '14', '7'];

type KpiAccent = 'navy' | 'green' | 'red' | 'blue' | 'amber';

const ACCENT_TOP: Record<KpiAccent, string> = {
  navy: 'bg-blueprint-blue',
  green: 'bg-galvanizing-green',
  red: 'bg-readable-red',
  blue: 'bg-[#2f6fe0]',
  amber: 'bg-thin-yellow',
};

function InsightsInfoTip({ label, tooltip }: { label: string; tooltip: string }) {
  return (
    <Tooltip
      content={tooltip}
      placement="top"
      contentClassName="!bg-blueprint-blue text-[12px] leading-snug max-w-[260px]"
    >
      <button
        type="button"
        className="inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full text-heritage-grey hover:text-blueprint-blue"
        aria-label={`About ${label}`}
      >
        <Info className="h-3.5 w-3.5" />
      </button>
    </Tooltip>
  );
}

function SummaryKpiCard({
  label,
  tooltip,
  value,
  sub,
  accent,
}: {
  label: string;
  tooltip: string;
  value: React.ReactNode;
  sub?: string;
  accent: KpiAccent;
}) {
  return (
    <div className="relative overflow-hidden rounded-[10px] border border-[#E4E9F0] bg-white p-4 shadow-sm">
      <div className={cn('absolute inset-x-0 top-0 h-[3px]', ACCENT_TOP[accent])} />
      <div className="mb-2.5 flex items-start justify-between gap-2">
        <div className="text-[10.5px] font-bold uppercase tracking-[0.06em] text-heritage-grey">
          {label}
        </div>
        <InsightsInfoTip label={label} tooltip={tooltip} />
      </div>
      <div className="mb-1.5 font-heading text-[26px] font-extrabold leading-none text-blueprint-blue tabular-nums">
        {value}
      </div>
      {sub ? <div className="text-[11px] text-heritage-grey">{sub}</div> : null}
    </div>
  );
}

function CountLine({
  label,
  count,
  flange,
  archive,
}: {
  label: string;
  count: number;
  flange?: number;
  archive?: number;
}) {
  return (
    <div className="flex items-baseline justify-between gap-2 text-[12px]">
      <span className="text-heritage-grey">{label}</span>
      <span className="font-semibold tabular-nums text-blueprint-blue">
        {count}
        <span className="font-normal text-heritage-grey">
          {pipeEndPresenceFlagSuffixText(flange ?? 0, archive ?? 0)}
        </span>
      </span>
    </div>
  );
}

function GroupCard({
  group,
  onClick,
}: {
  group: PipeStatusOverviewBucket;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="relative overflow-hidden rounded-[10px] border border-[#E4E9F0] bg-white p-4 text-left shadow-sm transition-all hover:border-blueprint-blue/35 hover:shadow-md"
    >
      <div className="absolute inset-x-0 top-0 h-[3px] bg-blueprint-blue" />
      <div className="mb-1 font-heading text-[14px] font-bold text-blueprint-blue">
        {formatOverviewGroupLabel(group.groupId)}
      </div>
      <div className="mb-3 font-heading text-[24px] font-extrabold leading-none tabular-nums text-blueprint-blue">
        {group.totalPipes}
        <span className="ml-1.5 text-[11px] font-semibold uppercase tracking-[0.05em] text-heritage-grey">
          pipes
        </span>
      </div>
      <div className="flex flex-col gap-1.5 border-t border-[#EDF1F6] pt-3">
        <CountLine
          label="Both"
          count={group.both}
          flange={group.bothFlange}
          archive={group.bothArchive}
        />
        <CountLine
          label="Only End A"
          count={group.endAOnly}
          flange={group.endAOnlyFlange}
          archive={group.endAOnlyArchive}
        />
        <CountLine
          label="Only End B"
          count={group.endBOnly}
          flange={group.endBOnlyFlange}
          archive={group.endBOnlyArchive}
        />
      </div>
    </button>
  );
}

function FilterField({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex min-w-0 flex-col gap-1">
      <span className="text-[11px] leading-none text-heritage-grey">{label}</span>
      {children}
    </div>
  );
}

export const PipeStatusOverviewPanel: React.FC<PipeStatusOverviewPanelProps> = ({
  project,
  pipeSizeOptions,
  onOpenGroups,
  refreshToken = 0,
}) => {
  const projectNumber = project?.project?.projectNumber ?? '';
  const projectId = project?.project?.projectId;
  const projectName = project?.project?.projectName ?? '';

  const [includeArchived, setIncludeArchived] = useState(false);
  const [selectedPipeSizeIds, setSelectedPipeSizeIds] = useState<string[]>([]);
  const [selectedGroupIds, setSelectedGroupIds] = useState<string[]>([]);
  const [endPresence, setEndPresence] = useState<OverviewEndPresenceFilter>('all');
  const [flange, setFlange] = useState<OverviewFlagFilter>('all');
  const [data, setData] = useState<PipeStatusOverviewResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [timeRange, setTimeRange] = useState<PipeUploadActivityTimeRange>('all');
  const [activity, setActivity] = useState<PipeUploadActivityResponse | null>(null);
  const [activityLoading, setActivityLoading] = useState(false);
  const [activityError, setActivityError] = useState<string | null>(null);

  const selectedGroupIdsForApi = useMemo(
    () =>
      selectedGroupIds.map((id) =>
        id === PIPE_STATUS_UNGROUPED_TOKEN ? '' : id
      ),
    [selectedGroupIds]
  );

  const selectedPipeIdsForApi = useMemo(
    () => selectedPipeSizeIds.filter(Boolean),
    [selectedPipeSizeIds]
  );

  const pipeSizeSelectOptions = useMemo(
    () =>
      pipeSizeOptions.map((opt) => ({
        value: String(opt.pipeId),
        label: opt.label,
      })),
    [pipeSizeOptions]
  );

  useEffect(() => {
    setSelectedGroupIds([]);
    setSelectedPipeSizeIds([]);
  }, [projectNumber, projectId]);

  const loadOverview = useCallback(async () => {
    if (!projectNumber && projectId == null) {
      setData(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const result = await fetchPipeStatusOverview({
        projectId,
        projectNumber,
        projectName,
        includeArchived,
        pipeIds: selectedPipeIdsForApi.length > 0 ? selectedPipeIdsForApi : undefined,
        groupIds: selectedGroupIdsForApi.length > 0 ? selectedGroupIdsForApi : undefined,
        endPresence,
        flange,
      });
      setData(result);
    } catch (err: any) {
      setError(err?.message || 'Failed to load overview');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [
    projectId,
    projectNumber,
    projectName,
    includeArchived,
    selectedPipeIdsForApi,
    selectedGroupIdsForApi,
    endPresence,
    flange,
  ]);

  const loadActivity = useCallback(async () => {
    if (!projectNumber && projectId == null) {
      setActivity(null);
      return;
    }
    setActivityLoading(true);
    setActivityError(null);
    try {
      const result = await fetchPipeUploadActivity({
        projectId,
        projectNumber,
        projectName,
        pipeIds: selectedPipeIdsForApi.length > 0 ? selectedPipeIdsForApi : undefined,
        timeRange,
      });
      setActivity(result);
    } catch (err: any) {
      setActivityError(err?.message || 'Failed to load upload activity');
      setActivity(null);
    } finally {
      setActivityLoading(false);
    }
  }, [projectId, projectNumber, projectName, selectedPipeIdsForApi, timeRange]);

  useEffect(() => {
    void loadOverview();
  }, [loadOverview, refreshToken]);

  useEffect(() => {
    void loadActivity();
  }, [loadActivity, refreshToken]);

  const groupOptions = useMemo(
    () =>
      (data?.availableGroupIds ?? []).map((id) => ({
        value: encodeOverviewGroupIdForQuery(id),
        label: formatOverviewGroupLabel(id),
      })),
    [data?.availableGroupIds]
  );

  const summary = data?.summary;
  const groups = data?.groups ?? [];
  const projectLabel = projectName || projectNumber || 'Project';

  const openWithSelection = (groupIds: string[]) => {
    onOpenGroups({
      groupIds,
      pipeIds: selectedPipeIdsForApi,
    });
  };

  return (
    <div className="space-y-5">
      {/* Single filter bar — Audit Trail style */}
      <div className="rounded-[10px] border border-[#E4E9F0] bg-white p-3 shadow-sm sm:p-4">
        <div className="flex flex-col gap-3 xl:flex-row xl:items-end xl:justify-between">
          <div className="flex flex-wrap items-end gap-2.5">
            <FilterField label="Pipe size">
              <div className="min-w-[200px]">
                <MultiSelect
                  options={pipeSizeSelectOptions}
                  selected={selectedPipeSizeIds}
                  onChange={setSelectedPipeSizeIds}
                  placeholder="All pipe sizes"
                  className="text-[12px]"
                />
              </div>
            </FilterField>

            <FilterField label="Groups">
              <div className="min-w-[180px]">
                <MultiSelect
                  options={groupOptions}
                  selected={selectedGroupIds}
                  onChange={setSelectedGroupIds}
                  placeholder="All groups"
                  className="text-[12px]"
                />
              </div>
            </FilterField>

            <FilterField label="Ends">
              <Select
                value={endPresence}
                onValueChange={(v: string) => setEndPresence(v as OverviewEndPresenceFilter)}
              >
                <SelectTrigger className="h-10 w-[140px] border-[#E2E5EA] bg-white text-[13px] font-semibold shadow-none">
                  <SelectValue placeholder="Ends" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All ends</SelectItem>
                  <SelectItem value="both">Only Both</SelectItem>
                  <SelectItem value="endAOnly">Only End A</SelectItem>
                  <SelectItem value="endBOnly">Only End B</SelectItem>
                </SelectContent>
              </Select>
            </FilterField>

            <FilterField label="Flange">
              <Select value={flange} onValueChange={(v: string) => setFlange(v as OverviewFlagFilter)}>
                <SelectTrigger className="h-10 w-[140px] border-[#E2E5EA] bg-white text-[13px] font-semibold shadow-none">
                  <SelectValue placeholder="Flange" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="yes">Flange</SelectItem>
                  <SelectItem value="no">Normal pipes</SelectItem>
                </SelectContent>
              </Select>
            </FilterField>

            <FilterField label="Period">
              <Select
                value={timeRange}
                onValueChange={(v: string) => setTimeRange(v as PipeUploadActivityTimeRange)}
              >
                <SelectTrigger className="h-10 w-[140px] border-[#E2E5EA] bg-white text-[13px] font-semibold shadow-none">
                  <Calendar className="mr-2 h-3.5 w-3.5 shrink-0 text-heritage-grey" />
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TIME_RANGE_OPTIONS.map((key) => (
                    <SelectItem key={key} value={key}>
                      {PIPE_UPLOAD_ACTIVITY_TIME_RANGE_LABELS[key]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FilterField>

            <label className="mb-2 inline-flex cursor-pointer select-none items-center gap-2 text-[12px] text-text-grey">
              <Checkbox
                checked={includeArchived}
                onCheckedChange={(checked) => setIncludeArchived(checked === true)}
                aria-label="Include archived pipes"
              />
              <span>Include archived</span>
            </label>
          </div>

          <Button
            size="sm"
            variant="outline"
            disabled={selectedGroupIdsForApi.length === 0}
            onClick={() => openWithSelection(selectedGroupIdsForApi)}
            className="h-10 border-[#E2E5EA] text-[12px]"
          >
            Open selected groups
          </Button>
        </div>
      </div>

      <PipeUploadActivityInsights
        data={activity}
        loading={activityLoading}
        error={activityError}
        onRetry={() => void loadActivity()}
      />

      <div className="space-y-4">
        <div className="min-w-0">
          <div className="flex items-center gap-1.5">
            <h3 className="font-heading text-[15px] font-bold text-blueprint-blue">Groups</h3>
            <InsightsInfoTip
              label="Groups"
              tooltip="Pipe groups for this project. Counts follow the filters above. Click a card to open that group’s pipes."
            />
          </div>
          <p className="text-[11.5px] text-heritage-grey">
            {projectLabel}
            {selectedPipeIdsForApi.length > 0
              ? ` · ${selectedPipeIdsForApi.length} pipe size${selectedPipeIdsForApi.length !== 1 ? 's' : ''}`
              : ' · All pipe sizes'}
          </p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center gap-2 py-16 text-heritage-grey">
            <Loader2 className="h-5 w-5 animate-spin" />
            <span className="text-sm">Loading groups…</span>
          </div>
        ) : error ? (
          <div className="rounded-[10px] border border-red-200 bg-red-50 p-6 text-center space-y-3">
            <p className="text-sm text-red-700">{error}</p>
            <Button size="sm" variant="outline" onClick={() => void loadOverview()}>
              Retry
            </Button>
          </div>
        ) : !summary || summary.totalPipes === 0 ? (
          <div className="rounded-[10px] border border-[#E4E9F0] bg-white p-8 text-center text-[13px] text-heritage-grey shadow-sm">
            {selectedGroupIds.length > 0 || selectedPipeSizeIds.length > 0
              ? 'No pipes match the current filters.'
              : 'No pipes for this project.'}
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-5">
              <SummaryKpiCard
                label="Total pipes"
                tooltip="Pipes matching the current filters."
                accent="navy"
                value={summary.totalPipes.toLocaleString()}
                sub="in filtered set"
              />
              <SummaryKpiCard
                label="Groups"
                tooltip="Number of groups represented in the filtered set."
                accent="blue"
                value={summary.groupCount.toLocaleString()}
                sub="with matching pipes"
              />
              <SummaryKpiCard
                label="Both ends"
                tooltip="Pipes that have both End A and End B."
                accent="green"
                value={summary.both.toLocaleString()}
                sub={
                  pipeEndPresenceFlagSuffixText(
                    summary.bothFlange ?? 0,
                    summary.bothArchive ?? 0
                  ).replace(/^\s*\(|\)$/g, '') || 'both ends present'
                }
              />
              <SummaryKpiCard
                label="Only End A"
                tooltip="Pipes that have End A only."
                accent="amber"
                value={summary.endAOnly.toLocaleString()}
                sub={
                  pipeEndPresenceFlagSuffixText(
                    summary.endAOnlyFlange ?? 0,
                    summary.endAOnlyArchive ?? 0
                  ).replace(/^\s*\(|\)$/g, '') || 'missing End B'
                }
              />
              <SummaryKpiCard
                label="Only End B"
                tooltip="Pipes that have End B only."
                accent="red"
                value={summary.endBOnly.toLocaleString()}
                sub={
                  pipeEndPresenceFlagSuffixText(
                    summary.endBOnlyFlange ?? 0,
                    summary.endBOnlyArchive ?? 0
                  ).replace(/^\s*\(|\)$/g, '') || 'missing End A'
                }
              />
            </div>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {groups.map((group) => (
                <GroupCard
                  key={group.groupId || '__ungrouped__'}
                  group={group}
                  onClick={() => openWithSelection([group.groupId])}
                />
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};
